import { useEffect, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { useDataProvider } from 'react-admin';
import { CircularProgress, Box, Typography } from '@mui/material';

interface TokenData {
  token: string;
  count: number;
  volume_usd: number;
}

const COLORS = [
  '#90caf9',
  '#f48fb1',
  '#ce93d8',
  '#80cbc4',
  '#a5d6a7',
  '#fff59d',
  '#ffab91',
  '#bcaaa4',
];

const API_URL = import.meta.env.VITE_API_URL || '/admin/api';

export const TokenDistributionChart = ({ userId }: { userId: number }) => {
  const [data, setData] = useState<TokenData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dataProvider = useDataProvider();

  useEffect(() => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    // Direct fetch to avoid double userId in URL
    fetch(`${API_URL}/users/${userId}/tokens`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    })
      .then((response) => response.json())
      .then((result: any) => {
        setData(result.data || []);
        setLoading(false);
      })
      .catch((err: any) => {
        console.error('Failed to load token distribution:', err);
        setError('Failed to load token data');
        setLoading(false);
      });
  }, [userId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 300,
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 300,
        }}
      >
        <Typography color="text.secondary">No token data available</Typography>
      </Box>
    );
  }

  const chartData = data.map((item) => ({
    name: item.token,
    value: item.count,
    volume: item.volume_usd,
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }: any) =>
            `${name}: ${percent ? (percent * 100).toFixed(0) : 0}%`
          }
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
        >
          {chartData.map((_entry: any, index: number) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: '#1e1e1e',
            border: '1px solid #333',
          }}
          itemStyle={{
            color: '#fff',
          }}
          labelStyle={{
            color: '#fff',
          }}
          formatter={(value: any, _name: any, props: any) => [
            `${value} bets ($${props.payload.volume.toFixed(2)})`,
            props.payload.name,
          ]}
        />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};
